import React, { Suspense } from "react";
import PropTypes from "prop-types";
import { Switch, Route, Redirect } from "react-router-dom";

import NotificationsFooter from "apps/notifications_footer";
import OkPhotoUploader from "shared/components/OkPhotoUploader";
import PageLoading from "shared/components/PageLoading";
import useExperiment from "shared/hooks/useExperiment";
import useSessionBlobUploader from "shared/hooks/useSessionBlobUploader";

import useScrollToTopOnNavigation from "routers/shared/hooks/useScrollToTopOnNavigation";
import usePayPalErrorHandler from "routers/shared/hooks/usePayPalErrorHandler";
import useInstantEvents from "routers/shared/hooks/useInstantEvents";
import useProfileQuickview from "routers/shared/hooks/useProfileQuickview";
import useRushHourModal from "routers/shared/hooks/useRushHourModal";
import useSiteRedirect from "routers/shared/hooks/useSiteRedirect";
import { useMarkSmsForcedMigration } from "analytics/sms";
import { ROUTES, UNAUTHED_ROUTE_LIST } from "routers/shared/util/consts";

const LegacyDoubletake = React.lazy(() => import(/* webpackChunkName: "spa_legacy_doubletake" */ "quickmatch"));
const Stacks = React.lazy(() => import(/* webpackChunkName: "spa_stacks" */ "apps/quickmatch"));
const Discovery = React.lazy(() => import(/* webpackChunkName: "spa_discovery" */ "apps/discovery"));
const Profile = React.lazy(() => import(/* webpackChunkName: "spa_profile" */ "apps/profile"));
const Likes = React.lazy(() => import(/* webpackChunkName: "spa_likes" */ "apps/userrows"));
const Messages = React.lazy(() => import(/* webpackChunkName: "spa_messages" */ "apps/messages"));
const StandaloneConversation = React.lazy(() => import(/* webpackChunkName: "spa_standalone_conversation" */ "apps/messenger/containers/Conversation"));
const MatchSearch = React.lazy(() => import(/* webpackChunkName: "spa_match_search" */ "matchsearch"));
const Settings = React.lazy(() => import(/* webpackChunkName: "spa_settings" */ "settings"));
const Upgrade = React.lazy(() => import(/* webpackChunkName: "spa_upgrade" */ "apps/upgrade"));
const Incognito = React.lazy(() => import(/* webpackChunkName: "spa_incognito" */ "apps/incognito_packages"));
const Onboarding = React.lazy(() => import(/* webpackChunkName: "spa_onboarding" */ "apps/onboarding"));
const Offboarding = React.lazy(() => import(/* webpackChunkName: "spa_offboarding" */ "apps/offboarding/App"));
const PhoneWall = React.lazy(() => import(/* webpackChunkName: "spa_phone_wall" */ "apps/phonewall"));
const LogoutTrigger = React.lazy(() => import(/* webpackChunkName: "spa_logout_trigger" */ "apps/logout_trigger"));
const NotFound = React.lazy(() => import(/* webpackChunkName: "spa_404" */ "./NotFound"));

const AuthedRoutes = ({ userid, location }) => {
	// Show Rush Hour modal, if needed.
	useRushHourModal(location, userid);

	// Handle any non-SPA redirects.
	useSiteRedirect(location);

	// Show PayPal errors, if needed.
	usePayPalErrorHandler(location);

	// Initialize InstantEvents.
	useInstantEvents();

	// Initialize Profile popover widget.
	useProfileQuickview();

	// Scroll to the top on each page nav.
	useScrollToTopOnNavigation(location);

	// Upload info about the current session.
	useSessionBlobUploader(userid);

	useMarkSmsForcedMigration(userid);

	// Decide which Doubletake experience to show.
	const [stacksExperimentGroup, { loading }] = useExperiment("STACKS_AB");
	const Doubletake = (() => {
		if (loading) {
			return PageLoading;
		} else if (stacksExperimentGroup === "TEST") {
			return Stacks;
		} else {
			return LegacyDoubletake;
		}
	})();

	return (
		<>
			<Suspense fallback={<PageLoading />}>
				<Switch>
					<Route
						exact
						path={ROUTES.Doubletake}
						component={Doubletake}
					/>
					<Route
						exact
						path={ROUTES.Discovery}
						component={Discovery}
					/>
					<Route
						exact
						path={ROUTES.MatchSearch}
						component={MatchSearch}
					/>
					<Route
						exact
						path={[
							...ROUTES.ProfileMain,
							...ROUTES.ProfileQuestions,
							...ROUTES.ProfilePhotos,
						]}
						component={Profile}
					/>
					<Route
						exact
						path={[
							...ROUTES.WhoLikesYou,
							...ROUTES.WhoYouLike,
							...ROUTES.Intros,
						]}
						component={Likes}
					/>
					<Route
						exact
						path={ROUTES.Messages}
						component={Messages}
					/>
					<Route
						exact
						path={ROUTES.StandaloneConversation}
						component={StandaloneConversation}
					/>
					<Route
						exact
						path={ROUTES.Settings}
						component={Settings}
					/>
					<Route
						exact
						path={ROUTES.Upgrade}
						component={Upgrade}
					/>
					<Route
						exact
						path={ROUTES.Incognito}
						component={Incognito}
					/>
					<Route
						exact
						path={ROUTES.Onboarding}
						component={Onboarding}
					/>
					<Route
						exact
						path={ROUTES.Offboarding}
						component={Offboarding}
					/>
					<Route
						exact
						path={ROUTES.PhoneWall}
						component={PhoneWall}
					/>
					<Route
						exact
						path={ROUTES.Logout}
						component={LogoutTrigger}
					/>

					{/* Legacy paths */}
					<Redirect exact from="/" to={ROUTES.Doubletake[0]} />
					<Redirect exact from="/home" to={ROUTES.Doubletake[0]} />
					<Redirect exact from="/quickmatch" to={ROUTES.Doubletake[0]} />
					<Redirect
						exact
						from="/stacks"
						to={{
							pathname: ROUTES.Doubletake[0],
							search: location.search,
						}}
					/>
					<Redirect exact from="/questions" to={`/profile/${userid}/questions`} />

					{/* Redirect all unauthed routes to Doubletake */}
					{UNAUTHED_ROUTE_LIST.map((unauthedRoute) => (
						<Redirect
							key={unauthedRoute}
							exact
							from={unauthedRoute}
							to={ROUTES.Doubletake[0]}
						/>
					))}

					<Route component={NotFound} />
				</Switch>
			</Suspense>
			<NotificationsFooter userid={userid} />
			<OkPhotoUploader userid={userid} />
		</>
	);
};

AuthedRoutes.propTypes = {
	userid: PropTypes.string.isRequired,
	location: PropTypes.object.isRequired,
};

export default AuthedRoutes;



// WEBPACK FOOTER //
// ./src/routers/Desktop/AuthedRoutes.jsx